import { useEffect } from "react";

type BreadcrumbItem = {
  name: string;
  path: string;
};

type SeoStructuredDataProps = {
  canonicalPath: string;
  description: string;
  title: string;
  breadcrumbs?: BreadcrumbItem[];
  includeFaqSchema?: boolean;
};

const SITE_URL = "https://justicedrive.co";
const SUPPORTED_STATES = ["California", "Florida", "Texas", "New York", "New Jersey"] as const;

const CALIFORNIA_ROUTE_PREFIXES = ["/california/", "/california-vehicle-code/", "/guides/", "/blog/"];
const CALIFORNIA_ROUTE_EXACT = new Set([
  "/speeding-ticket-cost-calculator/",
  "/trial-by-written-declaration-california/",
  "/tr-205-california/",
  "/mycitations-online-trial-by-declaration/",
  "/product/tr-205-defense/",
  "/violation/red-light/",
  "/violation/dui-under-21/",
  "/violation/speeding-ticket-california/",
  "/help/contest-speeding-ticket-ca/",
  "/help/traffic-ticket-dismissal-ca/",
  "/help/fight-traffic-ticket-ca/",
]);

const isCaliforniaSpecificRoute = (path: string) =>
  CALIFORNIA_ROUTE_EXACT.has(path) || CALIFORNIA_ROUTE_PREFIXES.some((prefix) => path.startsWith(prefix));

const administrativeArea = (name: string) => ({
  "@type": "AdministrativeArea",
  name,
});

const canonicalizePath = (path: string) => {
  if (!path || path === "/") {
    return "/";
  }
  return `${path.replace(/\/+$/, "")}/`;
};

const FAQ_ITEMS = [
  {
    question: "Is JusticeDrive legal advice?",
    answer: "No. JusticeDrive is software for workflow and document preparation and does not provide legal advice.",
  },
  {
    question: "Is JusticeDrive a law firm?",
    answer: "No. JusticeDrive is not a law firm and does not represent users in court.",
  },
  {
    question: "Are court fees included?",
    answer: "No. Court fees, fines, and penalties are not included and must be paid directly to the court or agency.",
  },
  {
    question: "Can I recover my purchase later?",
    answer: "Yes. Purchases are tied to your checkout email and can be recovered by secure magic-link access.",
  },
];

const upsertJsonLd = (id: string, payload: Record<string, unknown>) => {
  let script = document.head.querySelector(`script[data-seo-jsonld="${id}"]`) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-jsonld", id);
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(payload);
};

export default function SeoStructuredData({
  canonicalPath,
  description,
  title,
  breadcrumbs = [],
  includeFaqSchema = false,
}: SeoStructuredDataProps) {
  useEffect(() => {
    const pageUrl = `${SITE_URL}${canonicalPath}`;

    const organization = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "JusticeDrive",
      url: SITE_URL,
      logo: `${SITE_URL}/favicon.png`,
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "support@justicedrive.co",
        },
      ],
    };

    const website = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "JusticeDrive",
      url: SITE_URL,
    };

    const californiaSpecific = isCaliforniaSpecificRoute(canonicalPath);
    const service = {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: californiaSpecific
        ? "California traffic citation review and workflow software"
        : "traffic citation review and workflow software",
      name: title,
      provider: {
        "@type": "Organization",
        name: "JusticeDrive",
      },
      areaServed: californiaSpecific
        ? administrativeArea("California")
        : SUPPORTED_STATES.map(administrativeArea),
      url: pageUrl,
      description,
    };

    upsertJsonLd("organization", organization);
    upsertJsonLd("website", website);
    upsertJsonLd("service", service);

    if (breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: `${SITE_URL}${canonicalizePath(item.path)}`,
        })),
      };
      upsertJsonLd("breadcrumbs", breadcrumbSchema);
    } else {
      const existing = document.head.querySelector('script[data-seo-jsonld="breadcrumbs"]');
      existing?.remove();
    }

    if (includeFaqSchema) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.answer,
          },
        })),
      };
      upsertJsonLd("faq", faqSchema);
    } else {
      const existing = document.head.querySelector('script[data-seo-jsonld="faq"]');
      existing?.remove();
    }
  }, [breadcrumbs, canonicalPath, description, includeFaqSchema, title]);

  return null;
}
